import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import useMediaQuery from '../hooks/useMediaQuery';
import { DESKTOP_QUERY } from '../components/PageBar';
import { useAuth } from '../components/AuthContext';
import { DeskFrame, PhoneFrame } from '../components/AppFrame';
import { menuFor, pagesFor } from '../lib/menu';
import BuyerHome from './home/BuyerHome';
import ClientOrderingPage from './admin/Ordering/ClientOrderingPage';
import MovementProcessor from './admin/Movement/MovementProcessor';
import SaveFailedBar from '../components/SaveFailedBar';
import Appearance from './settings/Appearance';

/*
  A buyer's screens.

  Built 5 September 2026, on the owner's decision, closing the second of the two
  things left in "Where the app opens".

  **Until today a buyer got the rep's app.** `buyer` has been a real job level in
  the database since 21 August 2026, nobody has ever held it, and nobody had
  written down what one is for, so the menu had no entry and the app fell through
  to the rep: somebody whose job is ordering stock would have signed in to
  screens asking them to count it. Latent rather than live, and silent, which is
  the shape of fault this project keeps finding.

  **It renders the same three ordering pages an administrator sees**, imported
  rather than copied. A second copy of Replenishment is exactly the thing "One
  shell, one menu, three homes" was written to stop, and those three screens are
  still due their rebuild under "Build the Ordering area".

  The shell is the approved one: the two-tier bar on a desk, the dock and the
  page picker below 1024, both driven by the one menu in `src/lib/menu.jsx`.
*/
export default function BuyerDashboard() {
  const navigate  = useNavigate();
  const { profile } = useAuth();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);

  const AREAS    = menuFor('buyer');
  const KEYS     = pagesFor('buyer').map(p => p.key);

  // Home is where the app opens, for a buyer as for everybody, and a page that
  // is no longer in the menu falls back to it rather than showing nothing.
  const [tab, setTabRaw] = useState(() => {
    const saved = sessionStorage.getItem('buyerTab');
    return saved && KEYS.includes(saved) ? saved : 'home';
  });
  function setTab(k) { setTabRaw(k); sessionStorage.setItem('buyerTab', k); }


  const [cycle, setCycle] = useState(null);
  useEffect(() => {
    supabase.from('count_cycles').select('*').eq('status', 'open').maybeSingle()
      .then(({ data }) => setCycle(data || null));
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  // The navigation the frames draw (rebrand step 4, src/components/AppFrame.jsx).
  const nav = {
    areas: AREAS, activeKey: tab, onSelect: setTab,
    userName: profile?.full_name, email: profile?.email,
    roleLabel: 'Buyer', onSignOut: signOut,
  };

  const home = <BuyerHome profile={profile} cycle={cycle} onOpen={setTab} />;
  const page = (
    <>
      {tab === 'home' && home}
      {tab === 'client_ordering' && <ClientOrderingPage />}
      {tab === 'inventory_movement' && <MovementProcessor />}
      {tab === 'appearance' && <Appearance />}
    </>
  );

  const desktopLayout = (
    <DeskFrame nav={nav}    >
      <SaveFailedBar />{page}
    </DeskFrame>
  );

  const heldLayout = (
    <PhoneFrame nav={nav}
    >
        {/* The same bar below 1024. A component added to one of a screen's two
            layout trees and not the other is missing with nothing saying so,
            which is exactly what happened to the connection bar on this day. */}
        <SaveFailedBar />

        <div>{page}</div>
    </PhoneFrame>
  );

  // Only the tree for the current width is mounted. Hiding one with CSS alone
  // left it rendering, and its pages went on portalling their controls into the
  // visible bar. Same query PageBar tests, imported rather than retyped.
  return (
    <>
      <div className="rep-desktop-only">{isDesktop && desktopLayout}</div>
      <div className="rep-mobile-only">{!isDesktop && heldLayout}</div>
    </>
  );
}
